import React, { useEffect, useMemo, useState, useCallback } from 'react';
import clsx from 'clsx';
import Icon from '../components/Icon';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { supabase, isSupabaseConfigured } from '../lib/supabase';
import { timeAgo } from '../mock/extended';
import { PAGES, PAGE_LABEL, ROLE_LABEL, usePagePermissions } from '../hooks/usePagePermissions';
import AdminActivityLog from '../components/AdminActivityLog';
import TwoFactorSetup from '../components/TwoFactorSetup';

const STATUS_BADGE = {
  pending:   'badge-warning',
  approved:  'badge-success',
  rejected:  'badge-danger',
};

const ROLE_BADGE = {
  super_admin:        'badge-info',
  admin:              'badge-info',
  support_engineer:   'badge-warning',
  compliance_officer: 'badge-warning',
  member:             'badge-muted',
};

const ROLE_OPTIONS = [
  { value: 'member',             label: 'Member (Overview only)' },
  { value: 'support_engineer',   label: 'Support engineer (Overview + Support)' },
  { value: 'compliance_officer', label: 'Compliance officer (Overview + Compliance + Agent Reports)' },
  { value: 'super_admin',        label: 'Super admin (full access)' },
];

const fmtAbsolute = (d) => (d ? new Date(d).toLocaleString() : '—');

const Settings = () => {
  const { profile, signOut } = useAuth();
  const { theme, toggle } = useTheme();
  const { isSuperAdmin } = usePagePermissions();
  const isAdmin = isSuperAdmin; // super-admin gates all the user-management UI

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [statusTab, setStatusTab] = useState('all');
  const [query, setQuery] = useState('');
  const [confirming, setConfirming] = useState(null); // { id, action }
  const [permissionsTarget, setPermissionsTarget] = useState(null); // user object

  const load = useCallback(async () => {
    if (!isAdmin || !supabase) return;
    setLoading(true);
    setError(null);
    const { data, error } = await supabase
      .from('profiles')
      .select('id, email, full_name, status, role, created_at, approved_at')
      .order('created_at', { ascending: false });
    if (error) setError(error.message);
    else setUsers(data || []);
    setLoading(false);
  }, [isAdmin]);

  useEffect(() => { load(); }, [load]);

  const counts = useMemo(() => ({
    all:      users.length,
    pending:  users.filter((u) => u.status === 'pending').length,
    approved: users.filter((u) => u.status === 'approved').length,
    rejected: users.filter((u) => u.status === 'rejected').length,
  }), [users]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return users.filter((u) => {
      if (statusTab !== 'all' && u.status !== statusTab) return false;
      if (!q) return true;
      return (u.email || '').toLowerCase().includes(q) || (u.full_name || '').toLowerCase().includes(q);
    });
  }, [users, statusTab, query]);

  const approve = async (id) => {
    if (!supabase) return;
    const { error } = await supabase.rpc('admin_approve_user', { target_id: id });
    if (error) { setError(error.message); return; }
    load();
  };
  const reject = async (id, reason) => {
    if (!supabase) return;
    const { error } = await supabase.rpc('admin_reject_user', { target_id: id, reason: reason || null });
    if (error) { setError(error.message); return; }
    load();
  };

  const TABS = [
    { id: 'all',      label: 'All',       count: counts.all },
    { id: 'pending',  label: 'Pending',   count: counts.pending },
    { id: 'approved', label: 'Approved',  count: counts.approved },
    { id: 'rejected', label: 'Rejected',  count: counts.rejected },
  ];

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Settings</h1>
          <p>Operator preferences and access control.</p>
        </div>
        <button type="button" className="btn btn-outline" onClick={signOut}>
          Sign out
        </button>
      </div>

      <div className="dashboard-grid stagger mb-4" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))' }}>
        <div className="card">
          <div className="card-title">Your account</div>
          <div className="row row-gap-3" style={{ alignItems: 'center', flexWrap: 'wrap' }}>
            <div
              style={{
                width: 56, height: 56,
                borderRadius: '50%',
                background: 'var(--teal)',
                color: 'var(--navy-deep)',
                display: 'grid',
                placeItems: 'center',
                fontSize: 18,
                fontWeight: 800,
                flexShrink: 0,
              }}
            >
              {(profile?.email?.[0] || 'U').toUpperCase()}
            </div>
            <div className="col col-gap-2 flex-1" style={{ minWidth: 0 }}>
              <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--tx1)', wordBreak: 'break-word' }}>
                {profile?.email || '—'}
              </div>
              <div className="row row-gap-2">
                <span className={clsx('badge', ROLE_BADGE[profile?.role] || 'badge-muted')}>{profile?.role || '—'}</span>
                <span className={clsx('badge', STATUS_BADGE[profile?.status] || 'badge-muted')}>{profile?.status || '—'}</span>
              </div>
            </div>
          </div>
        </div>

        <TwoFactorSetup />

        <div className="card">
          <div className="card-title">Preferences</div>
          <div className="col col-gap-3">
            <div className="row" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
              <div className="col col-gap-2">
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--tx1)' }}>Theme</div>
                <div className="text-tx3 text-xs">Dark default for monitoring sessions</div>
              </div>
              <button
                type="button"
                className="btn btn-outline"
                style={{ padding: '8px 14px', fontSize: 12 }}
                onClick={toggle}
                aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
              >
                <Icon name={theme === 'dark' ? 'sun' : 'moon'} size={14} />
                {theme === 'dark' ? 'Light' : 'Dark'}
              </button>
            </div>
            <div className="card-divider" />
            <div className="row" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
              <div className="col col-gap-2">
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--tx1)' }}>Notifications</div>
                <div className="text-tx3 text-xs">Email + Slack alerts for compliance flags</div>
              </div>
              <span className="badge badge-muted">Coming soon</span>
            </div>
          </div>
        </div>
      </div>

      {!isAdmin && (
        <div className="card">
          <div className="empty">
            <div className="empty-icon"><Icon name="shield" size={26} /></div>
            <h3>Admin-only section</h3>
            <p>User management is available to admins. Contact stanford@coynitt.ca for elevated access.</p>
          </div>
        </div>
      )}

      {isAdmin && (
        <div className="card">
          <div className="row mb-3" style={{ justifyContent: 'space-between', flexWrap: 'wrap', gap: 'var(--s3)' }}>
            <div className="row row-gap-3">
              <Icon name="user" size={18} style={{ color: 'var(--teal)' }} />
              <div className="card-title" style={{ margin: 0 }}>User management</div>
            </div>
            <button type="button" className="btn btn-ghost" onClick={load} disabled={loading} aria-label="Refresh user list">
              {loading ? 'Refreshing…' : 'Refresh'}
            </button>
          </div>

          {error && (
            <div role="alert" style={{ background: 'var(--err-bg)', color: 'var(--err)', padding: 'var(--s3)', borderRadius: 'var(--r-md)', fontSize: 13, marginBottom: 'var(--s3)' }}>
              {error}
            </div>
          )}

          {!isSupabaseConfigured && (
            <div role="status" style={{ background: 'var(--warn-bg)', color: 'var(--warn)', padding: 'var(--s3)', borderRadius: 'var(--r-md)', fontSize: 13, marginBottom: 'var(--s3)' }}>
              Supabase isn't configured in this environment. User management requires the deployment env vars.
            </div>
          )}

          <div className="row" data-tabs="true" style={{ gap: 4, borderBottom: '1px solid var(--bd)', marginBottom: 16 }}>
            {TABS.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setStatusTab(tab.id)}
                style={{
                  padding: '10px 14px',
                  background: 'transparent',
                  color: statusTab === tab.id ? 'var(--teal)' : 'var(--tx2)',
                  fontWeight: statusTab === tab.id ? 700 : 500,
                  fontSize: 13,
                  borderBottom: statusTab === tab.id ? '2px solid var(--teal)' : '2px solid transparent',
                  marginBottom: -1,
                  cursor: 'pointer',
                  transition: 'color var(--tf) var(--ease)',
                }}
                aria-pressed={statusTab === tab.id}
              >
                {tab.label}
                <span className="badge badge-muted" style={{ marginLeft: 8 }}>{tab.count}</span>
              </button>
            ))}
          </div>

          <div className="row mb-3" style={{ position: 'relative' }}>
            <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--tx3)' }}>
              <Icon name="search" size={16} />
            </span>
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by email or name..."
              aria-label="Search users"
              style={{
                width: '100%',
                padding: '10px 14px 10px 36px',
                background: 'var(--bg-elevated)',
                border: '1px solid var(--bd)',
                borderRadius: 'var(--r-md)',
                color: 'var(--tx1)',
                fontSize: 13,
                outline: 'none',
              }}
            />
          </div>

          {loading && users.length === 0 ? (
            <div className="col col-gap-3">
              {[0, 1, 2].map((i) => (
                <div key={i} className="skeleton skeleton-block" style={{ height: 56 }} />
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <div className="empty">
              <div className="empty-icon"><Icon name="user" size={22} /></div>
              <h3 style={{ fontSize: 14 }}>No users in this view</h3>
              <p>{statusTab === 'all' ? 'No accounts have been created yet.' : `No ${statusTab} users matching the filter.`}</p>
            </div>
          ) : (
            <div className="table-wrap">
              <table style={{ minWidth: 720 }}>
                <thead>
                  <tr>
                    <th>Email</th>
                    <th>Role</th>
                    <th>Status</th>
                    <th>Created</th>
                    <th>Approved</th>
                    <th aria-label="Actions" />
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((u) => (
                    <tr key={u.id}>
                      <td style={{ color: 'var(--tx1)', fontWeight: 600, wordBreak: 'break-word' }}>{u.email}</td>
                      <td><span className={clsx('badge', ROLE_BADGE[u.role] || 'badge-muted')}>{ROLE_LABEL[u.role] || u.role}</span></td>
                      <td><span className={clsx('badge', STATUS_BADGE[u.status] || 'badge-muted')}>{u.status}</span></td>
                      <td title={fmtAbsolute(u.created_at)}>{timeAgo(u.created_at)}</td>
                      <td title={fmtAbsolute(u.approved_at)}>{u.approved_at ? timeAgo(u.approved_at) : '—'}</td>
                      <td>
                        <div className="row row-gap-2" style={{ flexWrap: 'wrap' }}>
                          {u.status === 'pending' && (
                            <>
                              <button type="button" className="btn btn-primary" style={{ padding: '6px 12px', fontSize: 12 }} onClick={() => approve(u.id)}>Approve</button>
                              <button type="button" className="btn btn-outline" style={{ padding: '6px 12px', fontSize: 12 }} onClick={() => setConfirming({ id: u.id, action: 'reject', email: u.email })}>Reject</button>
                            </>
                          )}
                          {u.status === 'rejected' && (
                            <button type="button" className="btn btn-ghost" style={{ padding: '6px 12px', fontSize: 12 }} onClick={() => approve(u.id)}>Restore</button>
                          )}
                          {u.status === 'approved' && (
                            <button type="button" className="btn btn-outline" style={{ padding: '6px 12px', fontSize: 12 }} onClick={() => setPermissionsTarget(u)}>
                              <Icon name="shield" size={12} /> Permissions
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {confirming && (
        <RejectModal
          email={confirming.email}
          onCancel={() => setConfirming(null)}
          onConfirm={(reason) => {
            reject(confirming.id, reason);
            setConfirming(null);
          }}
        />
      )}

      {permissionsTarget && (
        <PermissionsModal
          user={permissionsTarget}
          onClose={() => setPermissionsTarget(null)}
          onSaved={() => { setPermissionsTarget(null); load(); }}
        />
      )}

      {/* H-5 audit: immutable activity log of every admin action.
          Gated server-side by has_page_access('settings'); the read API
          short-circuits at 401/403 for non-permitted users so we render
          unconditionally and trust the API gate. */}
      <AdminActivityLog />
    </>
  );
};

const RejectModal = ({ email, onCancel, onConfirm }) => {
  const [reason, setReason] = useState('');
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="reject-title"
      onClick={onCancel}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'var(--bg-overlay)',
        display: 'grid',
        placeItems: 'center',
        zIndex: 1000,
        padding: 'var(--s4)',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="card"
        style={{ maxWidth: 420, width: '100%', padding: 'var(--s6)', animation: 'scaleIn var(--tb2) var(--spring-sm)' }}
      >
        <h2 id="reject-title" style={{ fontSize: 18, fontWeight: 800, color: 'var(--tx1)', marginBottom: 'var(--s2)' }}>
          Reject access?
        </h2>
        <p style={{ fontSize: 13, color: 'var(--tx2)', marginBottom: 'var(--s4)', lineHeight: 1.5 }}>
          {email} won't be able to access the dashboard until reinstated. Optionally provide a reason for the audit log.
        </p>
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Optional reason..."
          aria-label="Rejection reason"
          rows={3}
          style={{
            width: '100%',
            padding: '10px 14px',
            background: 'var(--bg-elevated)',
            border: '1px solid var(--bd)',
            borderRadius: 'var(--r-md)',
            color: 'var(--tx1)',
            fontSize: 13,
            outline: 'none',
            resize: 'vertical',
            fontFamily: 'var(--font)',
            marginBottom: 'var(--s4)',
          }}
        />
        <div className="row row-gap-2" style={{ justifyContent: 'flex-end' }}>
          <button type="button" className="btn btn-ghost" onClick={onCancel}>Cancel</button>
          <button type="button" className="btn btn-primary" onClick={() => onConfirm(reason.trim())}>
            Reject access
          </button>
        </div>
      </div>
    </div>
  );
};

// Email-notification event types — labels mirror the SQL enum values
// declared in 0006_notifications.sql.
const NOTIFICATION_EVENTS = [
  { value: 'transaction_suspicious',       label: 'Suspicious transactions',         hint: 'Compliance flags at high severity' },
  { value: 'transaction_fraud',            label: 'Fraud alerts',                    hint: 'Sanctions hits / critical-severity flags' },
  { value: 'transaction_money_laundering', label: 'Suspected money laundering',      hint: 'Critical large_transaction / STR candidates' },
  { value: 'support_ticket_new',           label: 'New support tickets',             hint: 'Every ticket arriving at support@coynitt.ca' },
  { value: 'support_ticket_emergency',     label: 'Emergency support tickets',       hint: 'Auto-flagged: outage, lost funds, frozen…' },
  { value: 'vendor_warning',               label: 'Vendor warnings',                 hint: 'SLA breaches, integration outages, failed-tx clusters' },
];
const NOTIFICATION_EVENT_KEYS = NOTIFICATION_EVENTS.map((e) => e.value);

// Super-admin-only modal: assign role + per-page grants + per-event
// email-notification subscriptions for a user.
const PermissionsModal = ({ user, onClose, onSaved }) => {
  const [role, setRole] = useState(user.role || 'member');
  const [pages, setPages] = useState(new Set());
  const [events, setEvents] = useState(new Set());
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const isTargetSuperAdmin = role === 'super_admin' || role === 'admin';

  useEffect(() => {
    if (!supabase) return;
    Promise.all([
      supabase.from('user_page_permissions').select('page').eq('user_id', user.id),
      supabase.from('notification_subscriptions').select('event_type, enabled').eq('user_id', user.id),
    ]).then(([pRes, eRes]) => {
      if (pRes.error) setError(pRes.error.message);
      else setPages(new Set((pRes.data || []).map((r) => r.page)));

      if (eRes.error) {
        // Likely the migration hasn't been applied yet — fail open.
        // eslint-disable-next-line no-console
        console.warn('[settings] notification_subscriptions read failed:', eRes.error.message);
      } else {
        setEvents(new Set((eRes.data || []).filter((r) => r.enabled).map((r) => r.event_type)));
      }
      setLoaded(true);
    });
  }, [user.id]);

  const togglePage = (p) => setPages((s) => {
    const n = new Set(s);
    if (n.has(p)) n.delete(p); else n.add(p);
    return n;
  });
  const toggleEvent = (e) => setEvents((s) => {
    const n = new Set(s);
    if (n.has(e)) n.delete(e); else n.add(e);
    return n;
  });

  const save = async () => {
    if (!supabase) return;
    setSaving(true);
    setError(null);
    try {
      // Update role.
      if (role !== user.role) {
        const { error: rErr } = await supabase.rpc('admin_set_user_role', { target_id: user.id, new_role: role });
        if (rErr) throw rErr;
      }
      // Set page grants (atomic replace).
      const { error: pErr } = await supabase.rpc('admin_set_page_permissions', {
        target_id: user.id,
        pages: Array.from(pages),
      });
      if (pErr) throw pErr;
      // Set notification subscriptions (atomic replace). Super admins
      // receive every event regardless — we still write rows for clarity
      // but the cron treats role>=admin as opt-in to all.
      const eventList = isTargetSuperAdmin ? NOTIFICATION_EVENT_KEYS : Array.from(events);
      const { error: nErr } = await supabase.rpc('admin_set_notification_subscriptions', {
        target_id: user.id,
        events: eventList,
      });
      if (nErr) {
        // If the RPC is missing the migration hasn't been applied. Surface
        // a friendlier message but don't block the role/pages updates.
        if (/function .* does not exist/i.test(nErr.message)) {
          setError(`Saved role + page grants, but notification subscriptions need migration 0006_notifications.sql applied first.`);
          onSaved?.();
          return;
        }
        throw nErr;
      }
      onSaved?.();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div role="dialog" aria-modal="true" onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'var(--bg-overlay)', display: 'grid', placeItems: 'center', zIndex: 1000, padding: 'var(--s4)' }}>
      <div onClick={(e) => e.stopPropagation()} className="card" style={{ maxWidth: 540, width: '100%', padding: 'var(--s6)' }}>
        <h2 style={{ fontSize: 18, fontWeight: 800, color: 'var(--tx1)', marginBottom: 'var(--s2)' }}>
          Permissions for {user.email}
        </h2>
        <p style={{ fontSize: 13, color: 'var(--tx2)', marginBottom: 'var(--s4)', lineHeight: 1.5 }}>
          Pick a role for the default page set, then optionally grant individual pages on top.
        </p>

        {error && <div role="alert" style={{ background: 'var(--err-bg)', color: 'var(--err)', padding: 'var(--s3)', borderRadius: 'var(--r-md)', fontSize: 13, marginBottom: 'var(--s3)' }}>{error}</div>}

        <div className="col col-gap-3">
          <div>
            <div className="text-xs" style={{ color: 'var(--tx3)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 }}>Role</div>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value)}
              style={{ width: '100%', padding: '10px 14px', background: 'var(--bg-elevated)', border: '1px solid var(--bd)', borderRadius: 'var(--r-md)', color: 'var(--tx1)', fontSize: 13, fontFamily: 'var(--font)', outline: 'none' }}
            >
              {ROLE_OPTIONS.map((opt) => (<option key={opt.value} value={opt.value}>{opt.label}</option>))}
            </select>
          </div>

          <div>
            <div className="text-xs" style={{ color: 'var(--tx3)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 }}>
              Additional page grants
            </div>
            <div className="text-xs" style={{ color: 'var(--tx3)', marginBottom: 'var(--s2)' }}>
              These grants are on top of role defaults. Super admin sees everything regardless.
            </div>
            <div className="row row-gap-2" style={{ flexWrap: 'wrap' }}>
              {PAGES.filter((p) => p !== 'overview' && p !== 'settings').map((p) => (
                <button
                  key={p}
                  type="button"
                  disabled={!loaded}
                  onClick={() => togglePage(p)}
                  className={clsx('badge', pages.has(p) ? 'badge-info' : 'badge-muted')}
                  style={{ padding: '6px 12px', fontSize: 12, cursor: 'pointer', border: 'none' }}
                >
                  {pages.has(p) ? '✓ ' : ''}{PAGE_LABEL[p]}
                </button>
              ))}
            </div>
          </div>

          <div>
            <div className="text-xs" style={{ color: 'var(--tx3)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 }}>
              Email notifications
            </div>
            <div className="text-xs" style={{ color: 'var(--tx3)', marginBottom: 'var(--s2)' }}>
              {isTargetSuperAdmin
                ? 'Super admin receives every alert — these toggles are locked on.'
                : 'Pick which dashboard alerts this user receives by email.'}
            </div>
            <div className="col col-gap-2">
              {NOTIFICATION_EVENTS.map((evt) => {
                const checked = isTargetSuperAdmin || events.has(evt.value);
                return (
                  <label
                    key={evt.value}
                    className="row row-gap-3"
                    style={{ alignItems: 'flex-start', padding: 'var(--s2) var(--s3)', background: 'var(--bg-elevated)', border: '1px solid var(--bd)', borderRadius: 'var(--r-md)', cursor: isTargetSuperAdmin ? 'not-allowed' : 'pointer', opacity: isTargetSuperAdmin ? 0.85 : 1 }}
                  >
                    <input
                      type="checkbox"
                      disabled={!loaded || isTargetSuperAdmin}
                      checked={checked}
                      onChange={() => toggleEvent(evt.value)}
                      style={{ marginTop: 2 }}
                    />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className="text-sm font-semibold" style={{ color: 'var(--tx1)' }}>{evt.label}</div>
                      <div className="text-xs" style={{ color: 'var(--tx3)' }}>{evt.hint}</div>
                    </div>
                  </label>
                );
              })}
            </div>
          </div>
        </div>

        <div className="row row-gap-2" style={{ justifyContent: 'flex-end', marginTop: 'var(--s5)', flexWrap: 'wrap' }}>
          <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button type="button" className="btn btn-primary" disabled={saving || !loaded} onClick={save}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Settings;
